// Ready-to-publish text templates for Screen 3.
// Generates 3 outputs (post / WhatsApp script / strategy) personalised with the
// user's data. Returns null when no template applies for the (giro, answers, date),
// signalling the UI to fall back to the existing ChatGPT prompt mode.

function todayInfo() {
  const now = new Date();
  return {
    day: now.getDay(),    // 0 Sun ... 6 Sat
    date: now.getDate(),  // 1..31
    hour: now.getHours(), // 0..23
  };
}

export function selectTemplate(businessType, answers) {
  const a = answers || {};
  const { day, date, hour } = todayInfo();

  if (businessType === "taqueria") {
    if (a.nocturno === "Sí" && a.domicilio === "Sí") return "TAQUERIA_NOCHE_DOMICILIO";
    if (date === 1 || date === 15) return "TAQUERIA_QUINCENA";
    return "TAQUERIA_DIA_CUALQUIERA";
  }
  if (businessType === "polleria") {
    if (date === 1 || date === 15) return "POLLERIA_QUINCENA";
    if (day === 6 || day === 0) return "POLLERIA_SABADO_FAMILIAR";
    return "POLLERIA_LUNES_TACO_DORADO";
  }
  if (businessType === "puesto") {
    if (date === 1 || date === 15) return "PUESTO_QUINCENA";
    if (hour < 16) return "PUESTO_MEDIODIA";
    return "PUESTO_DEFAULT";
  }
  if (businessType === "abarrotes") {
    if (date === 1 || date === 15) return "ABARROTES_QUINCENA";
    if (a.promociones === "Sí, siempre") return "ABARROTES_OFERTA";
    return "ABARROTES_DEFAULT";
  }
  if (businessType === "bar") {
    if (date === 1 || date === 15) return "BAR_QUINCENA";
    if (day === 6 || day === 0) return "BAR_FIN_SEMANA";
    if (day >= 1 && day <= 4) return "BAR_ENTRE_SEMANA";
    return "BAR_DEFAULT";
  }
  if (businessType === "estetica") {
    // Map "reto" → template. The three reto options today are:
    //   "Conseguir clientes nuevos"        → PROMO (atracción)
    //   "Que los clientes regresen"        → FIDELIZACION
    //   "Llenar la agenda entre semana"    → DIFERENCIADOR (competir vs otros lugares)
    if (a.reto === "Conseguir clientes nuevos") return "ESTETICA_PROMO";
    if (a.reto === "Que los clientes regresen") return "ESTETICA_FIDELIZACION";
    if (a.reto === "Llenar la agenda entre semana") return "ESTETICA_DIFERENCIADOR";
    return null;
  }
  return null;
}

// Pesos formatter — when the user hasn't provided a price, return the literal
// placeholder so the post stays readable and obviously-editable.
function fmtPrice(value, hasPrice) {
  if (!hasPrice) return "[agrega tu precio aquí]";
  return `$${Math.round(value)}`;
}

export function getTokens({ businessType, answers, city, ownerName, otherText }) {
  const a = answers || {};

  const NOMBRE =
    (ownerName && ownerName.trim()) || (otherText && otherText.trim()) || "tu negocio";
  const CIUDAD = (city && city.trim()) || "tu ciudad";

  let PRODUCTO_ESTRELLA;
  if (businessType === "taqueria") {
    PRODUCTO_ESTRELLA = "pastor";
  } else if (businessType === "polleria") {
    PRODUCTO_ESTRELLA = "pollo rostizado";
  } else if (businessType === "puesto") {
    const prod = (a.producto || "").toLowerCase();
    PRODUCTO_ESTRELLA =
      prod === "tacos"
        ? "tacos"
        : prod === "tortas"
          ? "tortas"
          : prod === "comida corrida"
            ? "comida corrida"
            : prod === "antojitos"
              ? "antojitos"
              : "comida del día";
  } else if (businessType === "estetica") {
    // Map the answered "servicios" option to a short noun that reads natural
    // inside Instagram/WhatsApp copy. Falls back to a generic word so the
    // template still works if the user skipped the question.
    const serv = (a.servicios || "").toLowerCase();
    PRODUCTO_ESTRELLA =
      serv === "corte de cabello"
        ? "corte"
        : serv === "tinte y coloración"
          ? "tinte"
          : serv === "manicure y pedicure"
            ? "manicure"
            : serv === "barba y afeitado"
              ? "barba"
              : "servicio";
  } else {
    PRODUCTO_ESTRELLA = "tu especialidad";
  }

  const WHATSAPP = "[agrega tu WhatsApp aquí]";
  let HORARIO;
  if (businessType === "taqueria") {
    HORARIO = a.nocturno === "Sí" ? "7pm a 2am" : "12pm a 9pm";
  } else if (businessType === "polleria") {
    HORARIO = "11am a 7pm";
  } else if (businessType === "puesto") {
    HORARIO = "10am a 6pm";
  } else if (businessType === "abarrotes") {
    HORARIO = "8am a 10pm";
  } else if (businessType === "bar") {
    HORARIO = "6pm a 2am";
  } else if (businessType === "estetica") {
    HORARIO = "10am a 8pm";
  } else {
    HORARIO = "[agrega tu horario]";
  }
  const VENTAJA = "[describe lo que te hace diferente]";
  const PROMOCION = "[describe tu promoción]";
  const DIA_PROMO_DEFAULT = "[día de tu promo]";
  // For BAR entresemana templates, surface today's weekday name (capitalised, plural)
  // so the post reads naturally. All other giros keep the editable placeholder.
  const WEEKDAYS = ["Domingos", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábados"];
  const { day: _day } = todayInfo();
  const DIA_PROMO =
    businessType === "bar" && _day >= 1 && _day <= 4 ? WEEKDAYS[_day] : DIA_PROMO_DEFAULT;

  // Read the user-provided PRECIO_1 (kept as digits-only string in answers.precio_1
  // by Screen2Questionnaire). When absent or invalid, every derived price falls back
  // to "[agrega tu precio aquí]" so the post stays readable and obviously-editable.
  const rawPrecio = a.precio_1;
  const parsedPrecio =
    rawPrecio != null && String(rawPrecio).trim() !== ""
      ? Number(String(rawPrecio).replace(/\D/g, ""))
      : NaN;
  const PRECIO_1 = Number.isFinite(parsedPrecio) && parsedPrecio > 0 ? parsedPrecio : null;
  const hasPrice = PRECIO_1 != null;
  const base = PRECIO_1 || 0;
  const PRECIO_COMBO_NOCHE = fmtPrice(base * 5 + 40, hasPrice);
  const PRECIO_KILO = fmtPrice(base * 10, hasPrice);
  const PRECIO_MEDIO = fmtPrice(base, hasPrice);
  const PRECIO_ENTERO = fmtPrice(base * 2 - 15, hasPrice);

  return {
    NOMBRE,
    CIUDAD,
    PRODUCTO_ESTRELLA,
    WHATSAPP,
    HORARIO,
    VENTAJA,
    PROMOCION,
    DIA_PROMO,
    PRECIO_COMBO_NOCHE,
    PRECIO_KILO,
    PRECIO_MEDIO,
    PRECIO_ENTERO,
  };
}

export function applyTokens(text, tokens) {
  return text.replace(/\{([A-Z_0-9]+)\}/g, (m, k) => (k in tokens ? tokens[k] : m));
}

// Build the optional financial-meta section that is prepended to the THIRD output
// (Estrategia de producto / Promoción / Producto y precios) of every generated bundle.
// Returns "" when the user did not answer both financial questions or selected
// "Prefiero no decirlo" on the income question — in those cases we never surface
// money figures, exactly as the spec mandates.
//
// Language rule (HARD): we always say "puedes generar" and "si consigues" — never
// "vas a ganar" nor "garantizamos". This is not phrasing nicety, it's compliance.
export function buildMetaSection(answers) {
  const a = answers || {};
  const ingresos = a.ingresos_actuales;
  const meta = a.meta_ingresos;
  if (!ingresos || !meta) return "";
  if (ingresos === "Prefiero no decirlo") return "";

  const rawPrecio = a.precio_1;
  const parsedPrecio =
    rawPrecio != null && String(rawPrecio).trim() !== ""
      ? Number(String(rawPrecio).replace(/\D/g, ""))
      : NaN;
  const precio = Number.isFinite(parsedPrecio) && parsedPrecio > 0 ? parsedPrecio : null;

  let body;
  if (precio != null) {
    const semana = precio * 3;
    const mes = precio * 12;
    body = `Si consigues 3 clientes nuevos con esta estrategia, puedes generar aproximadamente $${semana} extra.
En un mes serían $${mes} adicionales a lo que ya tienes.
NegoIA te cuesta $49 al mes.
Tú decides si vale la pena.`;
  } else {
    body = `Si consigues 3 clientes nuevos esta semana con esta estrategia, ya justificaste el costo mensual de NegoIA varias veces.`;
  }
  return `📊 TU META ESTA SEMANA:
${body}

`;
}

const TEMPLATES = {
  TAQUERIA_NOCHE_DOMICILIO: [
    {
      title: "Post listo para Instagram",
      text: `🌙 ¿Se te cruzó el antojo a esta hora? Tranqui, en {NOMBRE} todavía estamos prendidos.

🌮 {PROMOCION} — válido hasta las 2am o hasta que se acabe la carne 😅

🛵 Llevamos a toda {CIUDAD}, no cobramos extra si estás cerca del centro.

📲 {WHATSAPP} — mándame "quiero promo" y te confirmo en 2 minutos.

#TacosNocturnos #AntojoDeNoche #{CIUDAD}DeNoche #TacosADomicilio`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Claro que sí! 🛵 Llegamos a casi toda {CIUDAD}. El envío es gratis si estás cerca del centro. La promo de {PROMOCION} sigue vigente. ¿Qué te armo? Dime tu dirección y te doy tiempo exacto.`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO NOCHE DE TACOS — para esta semana:
Combo: 5 tacos de {PRODUCTO_ESTRELLA} + 2 refrescos
Precio sugerido: {PRECIO_COMBO_NOCHE}
Publica a las 9pm y 11:30pm
Mensaje: "Combo noche para 2 personas"
Objetivo: subir ticket nocturno`,
    },
  ],
  TAQUERIA_QUINCENA: [
    {
      title: "Post listo para Instagram",
      text: `💰 Cayó la quincena y en {NOMBRE} lo sabemos.

🌮 Kilo de {PRODUCTO_ESTRELLA} + tortillas para 4 personas

Lleva la salsa de la casa en bolsita para que en tu casa siga sabiendo igual de bueno. 👨‍👩‍👧‍👦

📍 {CIUDAD}, abierto {HORARIO}

📲 Aparta tu kilo al {WHATSAPP} (se acaban rápido en quincena)

#Quincena{CIUDAD} #TacosParaLlevar #KiloDeTacos #Taqueria{NOMBRE}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Sí, claro! 👌 El kilo de {PRODUCTO_ESTRELLA} te incluye las tortillas y la salsa verde aparte para que no se aguarden. ¿Para qué hora lo quieres? Te lo preparo calientito.`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO KILO QUINCENA:
Producto: kilo de {PRODUCTO_ESTRELLA} + 30 tortillas + salsa
Precio sugerido: {PRECIO_KILO}
Publica el día 14 y 30 a las 7pm
Mensaje: "Kilo para la familia, listo para llevar"
Registra el WhatsApp de cada comprador para avisarle antes de la siguiente quincena`,
    },
  ],
  TAQUERIA_DIA_CUALQUIERA: [
    {
      title: "Post listo para Instagram",
      text: `🔥 ¿A poco no se te antoja? 🔥

En {NOMBRE} acabamos de sacar el trompo y las tortillas están calientitas. 🌮👋

Ordena 5 y te damos 1 de {PRODUCTO_ESTRELLA} de cortesía hoy.

📍 {CIUDAD}

📲 {WHATSAPP}

#Tacos{CIUDAD} #AntojoDelDía #TacosDeAqui #Taqueria{NOMBRE}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Qué onda! 🙌 Abro de {HORARIO}. Con 3 tacos ya quedas bien servido. Si vienes en la tarde te doy la salsa verde que es la que más piden. ¿Te armo una orden?`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO TARDE ANTÓJADA:
Publica a las 4:30pm con foto del trompo
Combo: 5 tacos + 1 de cortesía
Solo de 4pm a 7pm para crear urgencia
Objetivo: subir de 3 tacos por cliente a 6`,
    },
  ],
  POLLERIA_SABADO_FAMILIAR: [
    {
      title: "Post listo para Instagram",
      text: `🍗 Sábado de pollo en {NOMBRE}

Medio pollo rostizado + papas + salsa chimichurri casera + tortillas

Alcanza para 2 personas bien servidas 😅

Pide antes de las 5pm que después se hace fila.

📍 {CIUDAD}

📲 {WHATSAPP} — aparta tu medio y te lo guardo calientito

#PolloRostizado{CIUDAD} #SabadoDePollo #Polleria{NOMBRE}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Qué tal! 👋 El medio pollo rostizado te incluye las papas y la salsa chimichurri que hacemos aquí mismo. Si lo quieres completo te descuento $15. ¿Te armo un medio o un entero? Dime para qué hora.`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO SÁBADO FAMILIAR:
Publica viernes 7pm y sábado 11am
Combo: medio pollo + papas + salsa + tortillas ilimitadas
Precio medio: {PRECIO_MEDIO}
Precio entero con descuento: {PRECIO_ENTERO}
Mensaje: "Medio pollo para 2, listo antes de las 5pm"
Registra pedidos WhatsApp para recontactar cada viernes`,
    },
  ],
  POLLERIA_QUINCENA: [
    {
      title: "Post listo para Instagram",
      text: `💰 QUINCENA = POLLO COMPLETO EN {NOMBRE}

🍗 Pollo entero + papas + ensalada de col + refrescos

Alcanza para 4 personas fácil.

📍 {CIUDAD}

📲 Solo hago 20 enteros por quincena para asegurar calidad — aparta el tuyo al {WHATSAPP}

#Quincena{CIUDAD} #PolloCompleto #CenaFamiliar #Polleria{NOMBRE}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Buena elección! 👌 El entero te incluye papas, ensalada de col, refrescos y las salsas aparte. Solo hago 20 enteros para asegurar calidad. ¿Te aparto uno? Dime para qué hora.`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO ENTEROS QUINCENA:
Publica el día 14 y 30 a las 6pm
Límite de 20 enteros crea escasez
Pide anticipo de $50 por WhatsApp para confirmar apartado
Registra compradores para avisar antes de cada quincena`,
    },
  ],
  POLLERIA_LUNES_TACO_DORADO: [
    {
      title: "Post listo para Instagram",
      text: `💸 Lunes de taco dorado en {NOMBRE}

La especialidad del lunes: pollo dorado en manteca, crujiente por fuera, jugoso por dentro. 🍗✨

📍 {CIUDAD}, {HORARIO}

📲 {WHATSAPP} — "los del lunes" y te los guardo

#LunesDeDorados #TacoDorado{CIUDAD} #EspecialidadLunes #Polleria{NOMBRE}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Sí! 👌 Los tacos dorados del lunes son la especialidad de la casa — pollo dorado en manteca de cerdo, quedan crujientes por fuera y jugosos por dentro. ¿Te armo 5 o 10? En 10 minutos están listos.`,
    },
    {
      title: "Estrategia de producto",
      text: `ESPECIALIDAD LUNES:
Nómbralo siempre "taco dorado, especialidad del lunes"
Publica lunes 10am y 1pm
Habla del proceso (manteca de cerdo, dorado crujiente) — eso genera confianza y antojo`,
    },
  ],
  PUESTO_MEDIODIA: [
    {
      title: "Post listo para Instagram",
      text: `⏱️ ¿Ya pensaste qué vas a comer hoy?

En {NOMBRE} te servimos en 5 minutos.
{PRODUCTO_ESTRELLA} con arroz, frijoles y tortillas. Calientito y listo.

📍 {CIUDAD}
⏰ {HORARIO}
📲 Aparta tu comida: {WHATSAPP}

#ComidaCasera{CIUDAD} #AlmuerzoExpress #PuestoDeComida #ComidaRica{CIUDAD}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Claro! 🍽️ Hoy tenemos {PRODUCTO_ESTRELLA} con arroz, frijoles y tortillas. Si me avisas antes te lo tengo listo al llegar, sin esperar. ¿Para qué hora lo quieres?`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO ALMUERZO EXPRESS:
Publica a las 10:30am y 12:30pm
Ventaja: "listo en 5 minutos si apartas"
Esto diferencia de fondas lentas y OXXO
Agrega agua del día como upsell natural`,
    },
  ],
  PUESTO_QUINCENA: [
    {
      title: "Post listo para Instagram",
      text: `💰 Quincena en {NOMBRE} = comes rico sin gastar de más.

{PRODUCTO_ESTRELLA} + guarnición completa para llevar a casa 🏠

📍 {CIUDAD}
📲 {WHATSAPP} — pedimos para llevar

#Quincena{CIUDAD} #ComidaParaLlevar #PuestoDeComida #SaboresDe{CIUDAD}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Sí, hacemos para llevar! 🛍️ {PRODUCTO_ESTRELLA} completo con guarnición, bien empacado para que llegue calientito a tu casa. ¿Para cuántas personas? Dime y te digo en cuánto está todo.`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO QUINCENA PARA LLEVAR:
Publica el 14 y 30 a las 6pm
Enfócate en familias que no quieren cocinar ese día
Ofrece "orden para 4 personas" como paquete — sube el ticket`,
    },
  ],
  PUESTO_DEFAULT: [
    {
      title: "Post listo para Instagram",
      text: `🍽️ Hoy en {NOMBRE}:
{PRODUCTO_ESTRELLA}

Hecho en el momento, con sazón de siempre. 😋

📍 {CIUDAD} — {HORARIO}
📲 {WHATSAPP}

#ComidaCasera #PuestoDeComida{CIUDAD} #SaboresLocales #Hecho{CIUDAD}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Buenas! 👋 Hoy tenemos {PRODUCTO_ESTRELLA}. Está recién hecho, con buen sazón. ¿Te armo una orden? Dime cuánto quieres y si es para comer aquí o llevar.`,
    },
    {
      title: "Estrategia de producto",
      text: `ACCIÓN DE HOY:
Toma foto de tu platillo del día con buena luz natural
Publícala antes de las 11am con el texto de arriba
Agrega tu ubicación exacta con referencia conocida del barrio
Eso atrae clientes nuevos de la zona`,
    },
  ],
  ABARROTES_QUINCENA: [
    {
      title: "Post listo para Instagram",
      text: `💰 Cayó la quincena y en {NOMBRE} ya tenemos todo lo que necesitas.

🛒 Surtido completo, precio justo, a dos pasos de tu casa.

¿Necesitas algo? Escríbenos y te decimos si lo tenemos 👇

📍 {CIUDAD}
📲 {WHATSAPP}

#Quincena{CIUDAD} #Abarrotes{NOMBRE} #TiendaDeBarrio #Surtido{CIUDAD}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Sí, lo tenemos! 🛒 Dime qué necesitas y te digo el precio. Si juntas varios productos te hago un precio especial de quincena. ¿Qué te falta en tu despensa?`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO QUINCENA ABARROTES:
Publica el 14 y 30 en la mañana
Arma un "combo despensa básica" con tus productos que más rotan
Precio combo menor que comprar separado — mueve inventario y sube ticket de una vez`,
    },
  ],
  ABARROTES_OFERTA: [
    {
      title: "Post listo para Instagram",
      text: `🔥 OFERTA DEL DÍA en {NOMBRE}

{PROMOCION}

Solo por hoy o hasta agotar existencias.

📍 {CIUDAD}
📲 Más info: {WHATSAPP}

#OfertaDelDia #Abarrotes{CIUDAD} #TiendaDeBarrio #{NOMBRE}Ofertas`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Sí, la oferta sigue vigente! 🎉 {PROMOCION}. Es solo por hoy así que si te interesa pásate o dime cuánto quieres y te lo aparto. ¿Qué necesitas?`,
    },
    {
      title: "Estrategia de producto",
      text: `CÓMO ACTIVAR ESTA OFERTA:
Publica temprano (antes de las 9am)
Pon límite de tiempo o cantidad para crear urgencia real
Manda el post a tu lista de difusión de WhatsApp primero — tus clientes habituales son los que más rápido responden`,
    },
  ],
  ABARROTES_DEFAULT: [
    {
      title: "Post listo para Instagram",
      text: `🏪 ¿Ya sabes que en {NOMBRE} tenemos todo lo que buscas?

Cerca de tu casa, precios justos, atención de confianza.

📍 {CIUDAD} — {HORARIO}
📲 {WHATSAPP}

#TiendaDeBarrio #Abarrotes{CIUDAD} #{NOMBRE} #CercaDeTuCasa`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Hola! 👋 Sí lo tenemos, pásate o dime qué necesitas y te digo precio. Si es bastante te puedo hacer un precio especial. ¿Qué andas buscando?`,
    },
    {
      title: "Estrategia de producto",
      text: `ACCIÓN ESTA SEMANA:
Manda un mensaje a tus clientes de WhatsApp preguntando qué producto les gustaría que tuvieras
Las respuestas te dicen exactamente qué comprar para la siguiente semana
Eso es inteligencia de mercado gratis`,
    },
  ],
  BAR_FIN_SEMANA: [
    {
      title: "Post listo para Instagram",
      text: `🍺 ¿Ya tienes plan para hoy?

En {NOMBRE} ya estamos listos para recibirte.

{VENTAJA} — el ambiente que buscabas 🎵

📍 {CIUDAD}
📲 Reserva tu mesa: {WHATSAPP}
⏰ {HORARIO}

#Bar{CIUDAD} #NocheDeViernes #{NOMBRE}Bar #SalidaDeNoche`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Hola! 🍺 Claro que sí, tenemos mesas disponibles. ¿Para cuántas personas vienen? Te aparto una buena zona. La entrada es libre y {VENTAJA}. ¿Para qué hora los espero?`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO FIN DE SEMANA:
Publica viernes a las 3pm y sábado a las 5pm
Foto del bar con ambiente — mesas, luces, la barra — activa el FOMO visual
Mensaje clave: "sin reservación pierdes mesa"
Límite de reservas crea urgencia real`,
    },
  ],
  BAR_QUINCENA: [
    {
      title: "Post listo para Instagram",
      text: `💰 Quincena en {NOMBRE} = la mejor excusa para salir. 🥃

{PROMOCION} solo esta quincena.

No lo pienses mucho — la quincena que viene ya se acabó. 😅

📍 {CIUDAD} — {HORARIO}
📲 {WHATSAPP}

#Quincena{CIUDAD} #Bar{NOMBRE} #PromoQuincena #NochesDe{CIUDAD}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Hola! 🥃 Sí, la promo de quincena sigue vigente: {PROMOCION}. Es solo estos días así que si quieren venir díganme para cuántos y les aparto mesa. ¿Esta noche o el fin de semana?`,
    },
    {
      title: "Estrategia de producto",
      text: `PROMO QUINCENA:
Publica el 1 y 15 a las 12pm y 7pm
La quincena dura 3-4 días de gasto real
Crea un "kit quincena": botella + hielo + refresco a precio especial
Registra WhatsApp de cada grupo para avisarles en la siguiente quincena`,
    },
  ],
  BAR_ENTRE_SEMANA: [
    {
      title: "Post listo para Instagram",
      text: `⚡ ¿Quién dijo que hay que esperar al viernes?

{DIA_PROMO} de {NOMBRE}: {PROMOCION} 🍺

Menos gente, mejor ambiente, misma buena vibra.

📍 {CIUDAD} — {HORARIO}
📲 {WHATSAPP}

#MartesDeBar #Entre{CIUDAD}Semana #{NOMBRE}Bar #PromoEntresemana`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Claro que sí! ⚡ Los {DIA_PROMO} tenemos {PROMOCION}. Hay menos gente que el fin de semana así que hay buenas mesas. ¿A qué hora pasan? Les aparto una tranquila para que estén a gusto.`,
    },
    {
      title: "Estrategia de producto",
      text: `ESTRATEGIA ENTRESEMANA:
Publica el día anterior a las 8pm
El argumento de venta no es el precio — es el ambiente tranquilo y sin fila
Ofrece botana de cortesía para grupos de 4+ que lleguen antes de las 9pm
Eso llena el bar temprano y crea ambiente que atrae a más clientes`,
    },
  ],
  BAR_DEFAULT: [
    {
      title: "Post listo para Instagram",
      text: `🍺 Esta noche en {NOMBRE}.

{VENTAJA} — el lugar que estabas buscando.

📍 {CIUDAD}
📲 {WHATSAPP}
⏰ {HORARIO}

#Bar{CIUDAD} #{NOMBRE} #NocheDe{CIUDAD} #SalidaLocal`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Hola! 🍺 Sí estamos abiertos, {HORARIO}. Tenemos {VENTAJA}. ¿Para cuántas personas vienen? Les tengo lista una buena mesa.`,
    },
    {
      title: "Estrategia de producto",
      text: `ACCIÓN DE HOY:
La foto que más convierte para un bar es el ambiente con gente — no el producto solo. Si tienes foto de una noche concurrida, úsala hoy.
Publica a las 6pm con el texto de arriba
Agrega siempre el horario de cierre para que sepan hasta cuándo pueden llegar`,
    },
  ],
  ESTETICA_PROMO: [
    {
      title: "Post listo para Instagram (antes/después)",
      text: `✨ Antes y después en {NOMBRE}

Cambio total con un buen {PRODUCTO_ESTRELLA} — el tipo de resultado que te hace voltear al espejo otra vez. 💁‍♀️

🎁 PRIMERA VISITA: 20% de descuento sólo presentando este post.

📍 {CIUDAD} — {HORARIO}
📲 Agenda tu cita: {WHATSAPP}

#Estetica{CIUDAD} #Barberia{CIUDAD} #AntesYDespues #{NOMBRE}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Hola! 💇 Claro que sí, soy de {NOMBRE}. Te puedo agendar tu primera visita con 20% de descuento si te animas esta semana. ¿Qué día y hora te queda bien? Tengo espacios entre {HORARIO}.`,
    },
    {
      title: "Estrategia de producto",
      text: `ESTRATEGIA DE ATRACCIÓN:
La foto antes/después es la que más convierte en este giro — pide permiso al cliente y publícala con su mismo ángulo en ambas fotos
Promo de primera visita = anzuelo, no precio normal. Tu meta es que la persona vuelva una segunda vez sin descuento
Después de la primera cita, guarda el WhatsApp del cliente y mándale recordatorio a las 3-4 semanas para reagendar`,
    },
  ],
  ESTETICA_FIDELIZACION: [
    {
      title: "Mensaje de WhatsApp para clientes frecuentes",
      text: `¡Hola! 💆 Soy de {NOMBRE}. Ya pasaron varias semanas desde tu último {PRODUCTO_ESTRELLA} — quería avisarte que tengo espacios esta semana.

🎁 Si llegas a tu hora (sin demoras) te hago 15% de descuento.

¿Qué día te queda bien? Me ajusto a tu horario, sólo dime y te aparto el lugar.`,
    },
    {
      title: "Post listo para Instagram",
      text: `🔁 Esta semana en {NOMBRE}: descuento por puntualidad.

Si llegas a tu hora, te bajamos 15% en tu {PRODUCTO_ESTRELLA}. Así de simple.

Reservar te ayuda a ti (sin esperar) y a nosotros (sin huecos en la agenda). Gana-gana. 🙌

📲 Aparta tu lugar: {WHATSAPP}
📍 {CIUDAD} — {HORARIO}

#{NOMBRE} #Estetica{CIUDAD} #ClientesFrecuentes`,
    },
    {
      title: "Estrategia de retención",
      text: `ESTRATEGIA DE FIDELIZACIÓN:
Manda el WhatsApp a TODOS los que no han venido en más de 4 semanas — uno por uno, no en grupo
El descuento por puntualidad cuida tu agenda: menos esperas, más citas al día
Lleva un cuadernito o lista con fechas de última visita por cliente — eso solo ya te pone arriba del 90% de tu competencia`,
    },
  ],
  ESTETICA_DIFERENCIADOR: [
    {
      title: "Post listo para Instagram (diferenciador)",
      text: `💡 Lo que nadie más hace en {CIUDAD} lo encuentras en {NOMBRE}.

Nuestra especialidad: {PRODUCTO_ESTRELLA} hecho con técnica, tiempo y detalle. No salimos volando — salimos bien.

No competimos por precio. Competimos por resultado. ✂️

📲 Agenda tu lugar: {WHATSAPP}
📍 {CIUDAD} — {HORARIO}

#{NOMBRE} #Especialistas{CIUDAD} #Estetica{CIUDAD}`,
    },
    {
      title: "Script de WhatsApp",
      text: `¡Hola! 💈 Soy de {NOMBRE}. Lo que nos diferencia es que dedicamos tiempo real a cada cliente — no atendemos a las prisas. Si buscas un {PRODUCTO_ESTRELLA} bien hecho, te puedo agendar esta semana. ¿Qué día te queda?`,
    },
    {
      title: "Estrategia de posicionamiento",
      text: `ESTRATEGIA DE DIFERENCIADOR:
Cuando compites por precio, siempre vas a perder con el más barato. Compite por algo que el barato no puede dar: tiempo, técnica, detalle, ambiente
En el post, nunca menciones a tu competencia por nombre — solo refuérzate a ti
Si publicas testimonio de cliente, mejor video corto que captura. Genera más confianza y comparte mejor`,
    },
  ],
};

export function generateReadyTexts(input) {
  const id = selectTemplate(input.businessType, input.answers);
  if (!id) return null;
  const tpl = TEMPLATES[id];
  if (!tpl) return null;
  const tokens = getTokens(input);
  // The financial META section is intentionally NOT prepended to the ready-text
  // body anymore — it used to leak into the clipboard copy. It stays available
  // via buildMetaSection() if the UI wants to render it as a visual motivation
  // banner above the card, but never inside the copyable text.
  return tpl.map((t) => ({
    title: t.title,
    text: applyTokens(t.text, tokens),
  }));
}
